import { formatActivityDate } from "@/shared/activityDate.ts";
import { type GetThreadResp, type ThreadStatus } from "@/types/thread.ts";
import { cardStatusToDerivedStatus, type DerivedThreadStatus } from "@/types/threadCardStatus.ts";

// TODO: these Date utils are getting out of hand - bring in Temporal?

export const getThreadName = (thread: GetThreadResp | undefined) => {
  if (!thread) {
    return "Unknown Thread Type";
  }
  if (thread.title) {
    return thread.title;
  }
  return "New thread";
};

/** Re-exported under its long-standing name; native's contact activity uses the same one. */
export const formatDateForThreadList = formatActivityDate;

export const statusColorClasses: Record<ThreadStatus, string> = {
  "to-review": "text-thread-status-to-review",
  "in-progress": "text-thread-status-in-progress",
  completed: "text-thread-status-completed",
  draft: "", // draft is not currently supported in the UI
  dead: "text-gray-500", // dead threads shown in gray
};

// Re-exported from its shared home so the existing call sites keep importing it
// from here, and native's Library renders the same label from the same table.
export { formatThreadStatus } from "@/shared/threadStatusLabel.ts";

export const getThreadActiveArtifact = (
  thread: GetThreadResp,
): NonNullable<NonNullable<GetThreadResp["activeMessage"]>["artifacts"]>[number] | undefined => {
  return thread?.activeMessage?.artifacts?.at(0);
};

const threadNeedsAttention = (thread: GetThreadResp) => {
  if (thread.activityState === "waitingForUser") {
    return true;
  }

  if (thread.activeMessage?.needsReply) {
    return true;
  }

  const activeArtifact = getThreadActiveArtifact(thread);
  if (
    activeArtifact &&
    "status" in activeArtifact &&
    activeArtifact.status === "waitingForUserAction"
  ) {
    return true;
  }
};

// High-level thread status for badges. Includes the server-derived `waiting`
// and `paused` states from the card viewmodel; when `thread.card` is absent
// (fetch paths that don't populate it), we fall back to client-side
// derivation from `activityState` / needs-reply signals.
// Re-exported from the shared source of truth so existing web imports keep
// their path; the type + mapping now live in `types/threadCardStatus.ts`.
export type { DerivedThreadStatus };

export const getThreadStatus = (thread: GetThreadResp): DerivedThreadStatus => {
  if (thread.card) {
    // archived (thread.status === "completed") is a separate UI concern from the
    // card-status badge; the rest maps 1:1 through the shared table.
    return cardStatusToDerivedStatus(thread.card.status, thread.status === "completed");
  }

  if (thread.status === "completed") {
    return "archived";
  }
  if (thread.activityState === "running" || thread.activityState === "waitingForExternalEvent") {
    return "inProgress";
  }

  if (threadNeedsAttention(thread)) {
    return "needsAttention";
  }

  return "completed";
};

export const getThreadPagePath = (threadId: number) => {
  return `/threads/${threadId}`;
};

/**
 * The canonical URL for a thread's participant roster. Callers name this path
 * and let the surrounding nav boundary decide the surface — a canvas card, the
 * right sidebar, or the full page. Inverse of the `threads` branch in
 * `canvasContentForPath`, which maps it back to a canvas token.
 */
export const getThreadParticipantsPath = (threadId: number) => {
  return `/threads/${threadId}/participants`;
};

/**
 * The largest id a thread can have: `threads.id` is a Postgres `integer` identity
 * column, so anything past this addresses no row. Bounding the client here keeps
 * an out-of-range URL on the not-found path instead of sending a request the
 * server can only reject.
 */
const MAX_THREAD_ID = 2_147_483_647;

/**
 * Read a thread id out of a URL segment or URN id, accepting only the canonical
 * form the app writes.
 *
 * Deliberately stricter than `Number`, which is not a validator: it reads `0x10`
 * and `1e3` as 16 and 1000, so a hand-edited or mistyped path would silently
 * load a *different* thread's data rather than fail. It also accepts `" 7 "` and
 * `-1`, neither of which addresses anything. Returns undefined for all of them,
 * so callers render not-found instead.
 *
 * The upper bound is {@link MAX_THREAD_ID} rather than a safe-integer check —
 * the tighter of the two, and it covers the same hazard, since past 2^53
 * `Number` rounds to a neighbouring value and long enough input becomes
 * `Infinity`.
 */
export const parseThreadId = (raw: string | undefined): number | undefined => {
  if (raw === undefined || !/^\d+$/.test(raw)) return undefined;
  const threadId = Number(raw);
  return threadId >= 1 && threadId <= MAX_THREAD_ID ? threadId : undefined;
};

/**
 * Extract the thread id from a thread-page pathname — `/threads/:id`, including
 * deeper routes like `/threads/:id/messages/...`. Returns undefined for
 * non-thread pages and the `/threads/__shell` placeholder route.
 *
 * Used by the Marker.io capture path to anchor a bug report's debug link to the
 * thread the user is actually viewing, rather than whatever happens to be in the
 * active-thread set.
 */
export const getThreadIdFromPath = (pathname: string): number | undefined => {
  const match = pathname.match(/^\/threads\/(\d+)(?:\/|$)/);
  // Through the same bound a `?thread=` param gets. `Number` alone accepted
  // `/threads/0` and ids past what the column holds, which mattered little
  // while this only anchored a bug report's debug link and matters now that it
  // decides which thread the shell renders.
  return match ? parseThreadId(match[1]) : undefined;
};

// Signature of a thread list's identity, used by `useRestoreScrollPosition` to
// drop a stored scroll offset when the list has changed underneath it (filter
// switched, threads added/removed) rather than restoring to a now-wrong spot.
export const getThreadListSignature = (threads: GetThreadResp[]) => {
  return JSON.stringify({
    firstItemKey: threads.at(0)?.id,
    lastItemKey: threads.at(-1)?.id,
    itemCount: threads.length,
  });
};
